import React, { useState } from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import { useValidateCustomErrorProcessing } from "src/api/connectApi/queries";
import { ExpandableCodeEditor } from "src/base-components/CodeInput/ExpandableCodeEditor";
import { Switch } from "src/base-components/Switch";
import {
  CodeValidationAction,
  type CodeValidationResult,
} from "src/connections/config/CodeValidationAction";
import {
  DEFAULT_HEADER_ENTRIES,
  type HeaderEntry,
  headerEntriesToRecord,
} from "src/connections/config/SampleHeadersEditor";
import { SampleResponseConfiguration } from "src/customConnectionNode/editorComponents/SampleResponseConfiguration";
import {
  CustomConnectionForm,
  DEFAULT_CUSTOM_ERROR_CODE,
} from "src/customConnectionNode/types";
import { useAuthoringContext } from "src/router/routerContextHooks";

type CustomErrorConfigurationProps = {
  immutable: boolean;
};

const DEFAULT_SAMPLE_RESPONSE = `{
  "error": "invalid_request",
  "message": "The provider rejected the request"
}`;

export const CustomErrorConfiguration: React.FC<
  CustomErrorConfigurationProps
> = ({ immutable }) => {
  const formProps = useFormContext<CustomConnectionForm>();
  const { workspace } = useAuthoringContext();
  const isActive = useWatch({
    control: formProps.control,
    name: "config.custom_error_config.active",
  });
  const validationCode = useWatch({
    control: formProps.control,
    name: "config.custom_error_config.code",
  });
  const [sampleResponse, setSampleResponse] = useState(DEFAULT_SAMPLE_RESPONSE);
  const [sampleResponseHeaderEntries, setSampleResponseHeaderEntries] =
    useState<HeaderEntry[]>(DEFAULT_HEADER_ENTRIES);
  const validateMutation = useValidateCustomErrorProcessing(workspace.base_url);
  const validationData = validateMutation.data;
  const responseBody = validationData?.success
    ? typeof validationData.response.body === "string"
      ? validationData.response.body
      : JSON.stringify(validationData.response.body, null, 2)
    : null;
  const responseText = validationData?.success
    ? [
        `Status code: ${validationData.response.status_code}`,
        "Body:",
        responseBody,
      ].join("\n")
    : null;
  const validationResult: CodeValidationResult | undefined = validationData
    ? validationData.success
      ? {
          success: true,
          content: (
            <div className="flex flex-col gap-2">
              <span>Validation succeeded.</span>
              <pre className="whitespace-pre-wrap font-mono text-xs">
                {responseText}
              </pre>
            </div>
          ),
        }
      : {
          success: false,
          message: `${validationData.error.type}: ${validationData.error.message}`,
        }
    : undefined;

  const handleValidate = () => {
    if (!validationCode) return;

    validateMutation.mutate({
      validation_code: validationCode,
      sample_response_body: sampleResponse,
      sample_response_headers: headerEntriesToRecord(
        sampleResponseHeaderEntries,
      ),
    });
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-inter-medium-12px text-gray-800">
            Throw custom errors
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            Script custom logic to throw errors in falsely successful provider
            responses cases
          </span>
        </div>
        <Controller
          control={formProps.control}
          name="config.custom_error_config.active"
          render={({ field }) => (
            <Switch
              dataLoc="custom-connection-throw-custom-errors"
              disabled={immutable}
              enabled={!!field.value}
              onChange={() => {
                const enabled = !field.value;
                field.onChange(enabled);
                if (
                  enabled &&
                  formProps.getValues("config.custom_error_config.code") ===
                    undefined
                ) {
                  formProps.setValue(
                    "config.custom_error_config.code",
                    DEFAULT_CUSTOM_ERROR_CODE,
                  );
                }
              }}
            />
          )}
        />
      </div>
      {isActive && (
        <div className="mt-4">
          <SampleResponseConfiguration
            headerEntries={sampleResponseHeaderEntries}
            immutable={immutable}
            value={sampleResponse}
            onHeaderEntriesChange={setSampleResponseHeaderEntries}
            onChange={setSampleResponse}
          />
          <Controller
            control={formProps.control}
            name="config.custom_error_config.code"
            render={({ field }) => (
              <ExpandableCodeEditor
                dataLoc="custom-connection-custom-error-code"
                disabled={immutable}
                expandedHeight="h-195"
                expandedWidth="w-250"
                height="h-96"
                language="python"
                placeholder="Write your custom error logic here..."
                title="Custom error validation code"
                value={field.value ?? ""}
                onBlur={field.onBlur}
                onChange={field.onChange}
              />
            )}
          />
          <CodeValidationAction
            dataLoc="validate-custom-error-button"
            disabled={immutable || !validationCode || !sampleResponse.trim()}
            loading={validateMutation.isPending}
            requestError={validateMutation.error}
            result={validationResult}
            onValidate={handleValidate}
          />
        </div>
      )}
    </div>
  );
};
